import { COVER_WAITING_TOO_LONG_HOURS, type DelegationView } from '@nest-recruit/shared';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import { ToneChip } from '../../../../components/Tone';
import { formatDate, formatDateTime, formatNumber, orEmpty } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { useEndDelegation, useRestoreDelegation } from '../../api/users-api';
import { COVER_STATUS_LABEL, coverStatus } from '../../cover-dates';
import { ChangeDatesButton } from './ChangeDatesButton';
import { coverTone } from './cover-tone';
import styles from './CoverList.module.css';

/** A row the server has not answered for yet carries this in place of an id; it has no actions until it has one. */
export const PENDING_COVER_PREFIX = 'pending-';

const WAITING_DAYS = COVER_WAITING_TOO_LONG_HOURS / 24;

interface CoverListProps {
  readonly covers: readonly DelegationView[];
  readonly now: number;
  readonly viewerId: string | null;
  readonly isAdmin: boolean;
}

/**
 * The list under the timeline: who, the stand-in, the dates and why, the status, how many approvals were sent on, and End now or
 * Cancel on the row. One markup: a table-like grid when there is room, a card per cover when there isn't (a container query, so it
 * follows the list's own width beside the form, not the window's).
 */
export function CoverList({ covers, now, viewerId, isAdmin }: CoverListProps): ReactNode {
  const end = useEndDelegation();
  const restore = useRestoreDelegation();
  const showToast = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const dismiss = useToasts((state) => state.dismiss);

  const endCover = (cover: DelegationView): void => {
    const wasActive = coverStatus(cover, now) === 'active';
    // Undo beats confirm (ADR 0002 rule 8). Review R6: Undo brings back this same cover (the server's restore), never a second one, so
    // "Sent on" is not counted twice and the stand-in is not told again. Pressed before the end has answered, it waits for it.
    let ended: Promise<boolean> = Promise.resolve(false);
    const undoId = showUndo(
      wasActive ? `Cover ended: ${cover.toUserName} no longer covers ${cover.fromUserName}` : `Cover cancelled for ${cover.fromUserName}`,
      () => {
        void ended.then((isEnded) => {
          if (!isEnded) return;
          restore.mutate(cover.id, {
            onError: (error) => showToast({ tone: 'danger', message: "The cover couldn't be put back", detail: error.message }),
          });
        });
      },
    );
    ended = end.mutateAsync(cover.id).then(
      () => true,
      (error: unknown) => {
        dismiss(undoId);
        showToast({
          tone: 'danger',
          message: wasActive ? "The cover wasn't ended" : "The cover wasn't cancelled",
          ...(error instanceof Error ? { detail: error.message } : {}),
        });
        return false;
      },
    );
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.list}>
        <div className={styles.head} aria-hidden>
          <span>Away</span>
          <span>Covered by</span>
          <span>Dates and why</span>
          <span>Status</span>
          <span className={styles.number}>Sent on</span>
          <span />
        </div>
        <ul className={styles.rows} aria-label="Cover arrangements">
          {covers.map((cover) => (
            <CoverRow key={cover.id} cover={cover} now={now} canManage={isAdmin || cover.fromUserId === viewerId} onEnd={endCover} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function CoverRow({
  cover,
  now,
  canManage,
  onEnd,
}: {
  readonly cover: DelegationView;
  readonly now: number;
  readonly canManage: boolean;
  readonly onEnd: (cover: DelegationView) => void;
}): ReactNode {
  const status = coverStatus(cover, now);
  const isPending = cover.id.startsWith(PENDING_COVER_PREFIX);
  const isOpen = status === 'active' || status === 'scheduled';
  const isPast = !isOpen;

  return (
    <li className={`${styles.row} ${isPast ? styles.past : ''}`} data-pending={isPending || undefined}>
      <span className={styles.who}>
        <span className={styles.cellLabel}>Away</span>
        <b className={styles.name}>{cover.fromUserName}</b>
      </span>
      <span className={styles.standIn}>
        <span className={styles.cellLabel}>Covered by</span>
        <span className={styles.name}>{cover.toUserName}</span>
      </span>
      <span className={styles.dates}>
        <span className={styles.cellLabel}>Dates</span>
        <span className={styles.window}>
          {formatDate(cover.startsAt)} → {formatDate(cover.endsAt)}
        </span>
        <span className={styles.why}>{orEmpty(cover.reason)}</span>
      </span>
      <span className={styles.status}>
        <ToneChip tone={coverTone(status)}>{COVER_STATUS_LABEL[status]}</ToneChip>
        {cover.waitingTooLong > 0 && (
          <span className={styles.waiting} title={`Approvals waiting on ${cover.fromUserName} for more than ${WAITING_DAYS} days`}>
            <ToneChip tone="urgent">{cover.waitingTooLong} waiting too long</ToneChip>
          </span>
        )}
        {cover.revokedAt !== null && <span className={styles.ended}>Ended {formatDateTime(cover.revokedAt)}</span>}
      </span>
      <span className={`${styles.sent} ${styles.number}`}>
        <span className={styles.cellLabel}>Sent on</span>
        {status === 'scheduled' ? '—' : formatNumber(cover.sentOn)}
      </span>
      <span className={styles.actions}>
        {isPending ? (
          <span className={styles.saving}>Saving…</span>
        ) : (
          canManage &&
          isOpen && (
            <>
              <ChangeDatesButton cover={cover} now={now} />
              <Button variant="outline" size="sm" icon={X} onClick={() => onEnd(cover)}>
                {status === 'active' ? 'End now' : 'Cancel'}
              </Button>
            </>
          )
        )}
      </span>
    </li>
  );
}
